import { Element, type PinSlot, type ThermalSpec } from './element';
import type { MnaMatrix } from './mna';
import { fmt } from './format';
import { THERMAL_V, limitJunction } from './elements';

/**
 * 출력단 동작 모드
 * - cv  : 정전압. 목표전압 vset 에 출력저항 rs 가 직렬로 달린 전원
 * - cc  : 정전류(전류 제한). imax 만큼 밀어냄
 * - off : 출력이 목표보다 높음 → 역류 방지로 전류 0
 *
 * tanh 같은 매끈한 함수로 한 번에 풀면 부하에 따라 뉴턴 반복이 0V↔10V 로 튀며 발산한다.
 * 대신 모드마다 선형 모델을 스탬프하고, 해를 보고 모드를 바꿔 다시 푸는 방식(구간 선형)을 쓴다.
 * 부하가 단조(저항, 전지 등)라면 모드 전환은 몇 번 안에 반드시 멈춘다.
 */
export type OutMode = 'cv' | 'cc' | 'off';

const FOUR_PIN = (a: string, b: string, c: string, d: string): PinSlot[] => [
  { t: 0, s: -1, label: a },
  { t: 0, s: 1, label: b },
  { t: 'end', s: -1, label: c },
  { t: 'end', s: 1, label: d },
];

const MAX_MODE_SWITCHES = 12;
const GND_G = 1000; // 내부 접지선 컨덕턴스 (1 mΩ)
const IN_G = 1e-5; // 입력 저항 100 kΩ

/** 4핀 전원 모듈 공통: [IN+, IN−, OUT+, OUT−] */
abstract class PowerModule extends Element {
  outMode: OutMode = 'cv';
  vin = 0;
  vout = 0;
  iin = 0;
  iout = 0;
  /** 뉴턴 반복 중 추정한 입력 전류 (고정점 반복으로 갱신) */
  protected iinIter = 0;
  private switches = 0;
  /** IN− → OUT− 내부 접지선으로 흐르는 전류 */
  private ignd = 0;

  override get pinCount(): number {
    return 4;
  }
  override get nonlinear(): boolean {
    return true;
  }
  override resetState(): void {
    this.outMode = 'cv';
    this.vin = 0;
    this.vout = 0;
    this.iin = 0;
    this.iout = 0;
    this.iinIter = 0;
  }
  override pinCurrents(): number[] {
    return [this.iin, -this.iin + this.ignd, -this.iout, this.iout - this.ignd];
  }
  /** 모듈 손실(발열). 양수 = 열로 버려지는 전력 */
  override get power(): number {
    return this.vin * this.iin - this.vout * this.iout;
  }

  /** 지금 출력단이 켜져 있어야 하는가 */
  protected abstract outputEnabled(): boolean;
  protected abstract vset(): number;
  protected abstract imax(): number;
  protected abstract rs(): number;
  /** 출력 상태가 정해졌을 때 입력이 끌어오는 전류 */
  protected abstract inputCurrentFor(vin: number, x: number, iout: number): number;

  override beginStep(): void {
    this.switches = 0;
  }

  protected read(m: MnaMatrix): { vin: number; x: number } {
    const [ip, im, op, om] = this.nodes;
    return {
      vin: m.nodeVoltage(ip) - m.nodeVoltage(im),
      x: m.nodeVoltage(op) - m.nodeVoltage(om),
    };
  }

  protected outputCurrent(x: number): number {
    if (!this.outputEnabled()) return 0;
    switch (this.outMode) {
      case 'cv': return (this.vset() - x) / this.rs();
      case 'cc': return this.imax();
      default: return 0;
    }
  }

  override stamp(m: MnaMatrix): void {
    const [ip, im, op, om] = this.nodes;
    // 실제 모듈처럼 IN− 와 OUT− 는 기판 안에서 공통 접지 (1mΩ)
    m.conductance(im, om, GND_G);
    // 입력단 누설/분압 저항 (100kΩ) — 입력이 끊기면 전압이 0으로 떨어지게
    m.conductance(ip, im, IN_G);
    if (this.outputEnabled()) {
      if (this.outMode === 'cv') {
        const g = 1 / this.rs();
        m.conductance(op, om, g);
        m.currentSource(om, op, this.vset() * g);
      } else if (this.outMode === 'cc') {
        m.currentSource(om, op, this.imax());
      }
    }
    // 입력: IN+ 로 들어가 IN− 로 나오는 전류
    if (this.iinIter !== 0) m.currentSource(ip, im, this.iinIter);
  }

  /** 반복 중 입력이 사라졌으면 즉시 끄고 true (켜지는 건 스텝 끝에서만 — 떨림 방지) */
  protected abstract inputLost(vin: number): boolean;

  override updateNewton(m: MnaMatrix): number {
    const { vin, x } = this.read(m);
    let changed = 0;
    if (this.outputEnabled() && this.inputLost(vin)) {
      this.iinIter = 0;
      return 1;
    }

    if (this.outputEnabled() && this.switches < MAX_MODE_SWITCHES) {
      const i = this.outputCurrent(x);
      let next: OutMode = this.outMode;
      if (this.outMode === 'cv') {
        if (i > this.imax()) next = 'cc';
        else if (i < 0) next = 'off';
      } else if (this.outMode === 'cc') {
        if (x > this.vset() - this.imax() * this.rs()) next = 'cv';
      } else if (x < this.vset()) {
        next = 'cv';
      }
      if (next !== this.outMode) {
        this.outMode = next;
        this.switches++;
        changed = 1;
      }
    }

    const iout = this.outputCurrent(x);
    const newIin = this.outputEnabled() ? this.inputCurrentFor(vin, x, iout) : 0;
    const d = Math.abs(newIin - this.iinIter);
    this.iinIter = newIin;
    return Math.max(changed, d);
  }

  override endStep(m: MnaMatrix): void {
    const { vin, x } = this.read(m);
    const [, im, , om] = this.nodes;
    this.ignd = GND_G * (m.nodeVoltage(im) - m.nodeVoltage(om));
    this.vin = vin;
    this.vout = x;
    this.iout = this.outputCurrent(x);
    this.iin = this.iinIter + vin * IN_G;
    this.current = this.iout;
    this.voltageDrop = vin - x;
    this.afterStep(vin, x);
  }

  /** 모듈별 상태 기계 (입력 감지, 충전 완료 등) */
  protected abstract afterStep(vin: number, x: number): void;
}

/* ═══════════════════════════ 충전 모듈 ═══════════════════════════ */

export type ChargeMode = '입력 없음' | '정전류 충전' | '정전압 충전' | '충전 완료';

/**
 * TP4056 같은 리튬이온 선형 충전 모듈. 핀: IN+, IN−, B+, B−
 * - 입력이 minInput 이상이면 동작
 * - 정전류(CC) → cvVoltage 도달하면 정전압(CV) → 전류가 termination 밑으로 떨어지면 완료
 * - 선형 충전기라 입력전류 = 출력전류 + 대기전류 → 전압 차이는 전부 열
 */
export class ChargerModule extends PowerModule {
  mode: ChargeMode = '입력 없음';
  /** 온도 때문에 전류를 줄이는 중 */
  hot = false;
  private enabled = false;
  private full = false;

  override pinLayout(): PinSlot[] {
    return FOUR_PIN('IN+', 'IN−', 'B+', 'B−');
  }
  override resetState(): void {
    super.resetState();
    this.mode = '입력 없음';
    this.enabled = false;
    this.full = false;
  }
  protected outputEnabled(): boolean {
    return this.enabled && !this.full;
  }
  protected vset(): number {
    return this.num('cvVoltage', 4.2);
  }
  /** 온도 제한: 115℃ 넘으면 충전 전류를 줄인다 (TP4056 의 thermal regulation) */
  get thermalFactor(): number {
    return this.temp <= 115 ? 1 : Math.max(0.05, 1 - (this.temp - 115) / 25);
  }
  protected imax(): number {
    return Math.max(this.num('chargeCurrent', 1), 1e-6) * this.thermalFactor;
  }
  protected rs(): number {
    return Math.max(this.num('rs', 0.02), 1e-3);
  }
  /** SOP-8 작은 기판: 80℃/W, τ ≈ 20초 */
  override thermalSpec(): ThermalSpec | null {
    return { rth: 80, cth: 0.25 };
  }
  protected inputCurrentFor(_vin: number, _x: number, iout: number): number {
    return Math.max(iout, 0) + 0.0003;
  }
  protected inputLost(vin: number): boolean {
    if (vin >= this.num('minInput', 4.4) - 0.1) return false;
    this.enabled = false;
    return true;
  }

  protected afterStep(vin: number, x: number): void {
    const minIn = this.num('minInput', 4.4);
    const was = this.enabled;
    this.enabled = vin >= (was ? minIn - 0.1 : minIn);
    if (this.enabled && !was) this.outMode = 'cc';
    if (!this.enabled) this.iinIter = 0;

    const term = this.num('termination', 0.1);
    if (!this.enabled) {
      this.full = false;
      this.mode = '입력 없음';
      return;
    }
    if (this.full) {
      if (x < this.vset() - 0.15) {
        this.full = false; // 전압이 떨어지면 재충전
        this.outMode = 'cc';
      }
    } else if (this.outMode !== 'cc' && this.iout < term && x > this.vset() - 0.1) {
      this.full = true;
      this.iinIter = 0.0003;
    }
    this.mode = this.full ? '충전 완료' : this.outMode === 'cc' ? '정전류 충전' : '정전압 충전';
    if (this.charging && this.thermalFactor < 1) this.hot = true;
    else this.hot = false;
  }

  get charging(): boolean {
    return this.mode === '정전류 충전' || this.mode === '정전압 충전';
  }

  info(): string[] {
    return [
      '충전 모듈 (CC/CV)',
      `상태 ${this.mode}${this.hot ? ` (온도 제한 ${(this.thermalFactor * 100).toFixed(0)}%)` : ''}`,
      `입력 ${fmt(this.vin, 'V')} · ${fmt(this.iin, 'A')}`,
      `배터리 ${fmt(this.vout, 'V')} · ${fmt(this.iout, 'A')}`,
      `설정 ${fmt(this.imax(), 'A')} / ${fmt(this.vset(), 'V')}`,
      `발열 ${fmt(Math.max(0, this.power), 'W')}`,
    ];
  }
}

/* ═══════════════════════════ DC-DC 컨버터 ═══════════════════════════ */

/**
 * 스위칭 DC-DC 컨버터(승압/강압 겸용)의 평균 모델. 핀: IN+, IN−, OUT+, OUT−
 * - 출력: 목표전압 vout, 전류 제한 imax
 * - 입력: 출력전력 ÷ 효율 만큼 끌어온다 (입력전류 = Pout / (η·Vin) + 대기전류)
 * - 입력이 uvlo 밑으로 떨어지면 꺼진다 (배터리 과방전 보호)
 */
export class DcDcModule extends PowerModule {
  private enabled = false;
  /** 과열 차단 중 */
  overheated = false;

  override pinLayout(): PinSlot[] {
    return FOUR_PIN('IN+', 'IN−', 'OUT+', 'OUT−');
  }
  override resetState(): void {
    super.resetState();
    this.enabled = false;
  }
  protected outputEnabled(): boolean {
    return this.enabled && !this.overheated;
  }
  protected vset(): number {
    return this.num('vout', 5);
  }
  /** 작은 승압 모듈: 50℃/W, τ ≈ 25초. 150℃ 에서 꺼지고 120℃ 까지 식으면 다시 켜짐 */
  override thermalSpec(): ThermalSpec | null {
    return { rth: 50, cth: 0.5 };
  }
  override afterAccept(dt: number, ambient: number): void {
    super.afterAccept(dt, ambient);
    if (!this.overheated && this.temp > 150) this.overheated = true;
    else if (this.overheated && this.temp < 120) this.overheated = false;
  }
  protected imax(): number {
    return Math.max(this.num('imax', 2), 1e-6);
  }
  protected rs(): number {
    return Math.max(this.num('rout', 0.05), 1e-3);
  }
  private eta(): number {
    return Math.min(1, Math.max(0.05, this.num('efficiency', 0.9)));
  }
  protected inputCurrentFor(vin: number, x: number, iout: number): number {
    const pout = Math.max(x, 0) * Math.max(iout, 0);
    return pout / (this.eta() * Math.max(vin, 0.3)) + 0.002;
  }

  protected inputLost(vin: number): boolean {
    if (vin >= this.num('uvlo', 2.9)) return false;
    this.enabled = false;
    return true;
  }

  protected afterStep(vin: number): void {
    const uvlo = this.num('uvlo', 2.9);
    const was = this.enabled;
    this.enabled = vin >= (was ? uvlo : uvlo + 0.15);
    if (!this.enabled || this.overheated) this.iinIter = 0;
    if (this.enabled && !was) this.outMode = 'cv';
  }

  get running(): boolean {
    return this.enabled && !this.overheated;
  }

  info(): string[] {
    const pin = this.vin * this.iin;
    const pout = this.vout * this.iout;
    const state = this.overheated ? '과열 차단 🔥' : !this.enabled ? '꺼짐 (입력 부족)' : this.outMode === 'cc' ? '전류 제한' : this.outMode === 'off' ? '대기' : '동작';
    return [
      'DC-DC 컨버터',
      `상태 ${state}`,
      `입력 ${fmt(this.vin, 'V')} · ${fmt(this.iin, 'A')} · ${fmt(pin, 'W')}`,
      `출력 ${fmt(this.vout, 'V')} · ${fmt(this.iout, 'A')} · ${fmt(pout, 'W')}`,
      `효율 ${pin > 1e-6 ? ((pout / pin) * 100).toFixed(1) : '—'}%`,
      `발열 ${fmt(Math.max(0, pin - pout), 'W')}`,
    ];
  }
}


/* ═══════════════════════════ 배터리 보호회로 (BMS) ═══════════════════════════ */

export type BmsState = '정상' | '과충전 차단' | '과방전 차단' | '과전류 차단';

const BODY_IS = 1e-12;
const BODY_VT = THERMAL_V * 1.5;

/**
 * DW01 + 듀얼 MOSFET 같은 1셀 리튬 보호회로. 핀: B+, B− (셀) / P+, P− (바깥)
 * - B+ 와 P+ 는 그냥 연결, B− 와 P− 사이에 MOSFET 두 개(충전용·방전용)
 * - 셀 > 4.3V → 충전 FET 끔 / 셀 < 2.5V → 방전 FET 끔 / 과전류 → 둘 다 1초간 끔
 * - FET 하나만 꺼졌을 때는 그 FET 의 바디 다이오드(≈0.6V)로 반대 방향 전류가 흐른다.
 *   그래서 방전으로 꺼진 셀도 충전기를 꽂으면 충전이 시작된다.
 */
export class BmsModule extends Element {
  state: BmsState = '정상';
  private timer = 0;
  private vd = 0; // 바디 다이오드 동작점 (애노드 − 캐소드)
  vcell = 0;
  iswitch = 0; // P− → B− 방향 (방전이 양수)
  private ilink = 0;

  override get pinCount(): number {
    return 4;
  }
  override pinLayout(): PinSlot[] {
    return FOUR_PIN('B+', 'B−', 'P+', 'P−');
  }
  override get nonlinear(): boolean {
    return true;
  }
  override resetState(): void {
    this.state = '정상';
    this.timer = 0;
    this.vd = 0;
    this.vcell = 0;
    this.iswitch = 0;
    this.ilink = 0;
  }
  private ron(): number {
    return Math.max(this.num('ron', 0.03), 1e-3);
  }

  /** 다이오드 모드일 때 [애노드, 캐소드] 노드. 과방전 = 충전 방향(B−→P−)만, 과충전 = 방전 방향(P−→B−)만 */
  private diodeNodes(): [number, number] | null {
    const [, bm, , pm] = this.nodes;
    if (this.state === '과방전 차단') return [bm, pm];
    if (this.state === '과충전 차단') return [pm, bm];
    return null;
  }

  override stamp(m: MnaMatrix): void {
    const [bp, bm, pp, pm] = this.nodes;
    m.conductance(bp, pp, GND_G);
    m.conductance(pm, bm, 1e-9);
    if (this.state === '정상') {
      m.conductance(pm, bm, 1 / this.ron());
      return;
    }
    const dn = this.diodeNodes();
    if (!dn) return; // 과전류: 둘 다 꺼짐
    const v = this.vd;
    const ex = Math.exp(Math.min(v / BODY_VT, 60));
    const i = BODY_IS * (ex - 1);
    const g = Math.max((BODY_IS / BODY_VT) * ex, 1e-12);
    m.conductance(dn[0], dn[1], g);
    m.currentSource(dn[0], dn[1], i - g * v);
  }

  override updateNewton(m: MnaMatrix): number {
    const dn = this.diodeNodes();
    if (!dn) return 0;
    const vNew = m.nodeVoltage(dn[0]) - m.nodeVoltage(dn[1]);
    const vcrit = BODY_VT * Math.log(BODY_VT / (Math.SQRT2 * BODY_IS));
    const lim = limitJunction(vNew, this.vd, BODY_VT, vcrit);
    const d = Math.abs(lim - this.vd);
    this.vd = lim;
    return d;
  }

  override endStep(m: MnaMatrix, dt: number): void {
    const [bp, bm, pp, pm] = this.nodes;
    const v = (n: number) => m.nodeVoltage(n);
    this.vcell = v(bp) - v(bm);
    this.ilink = (v(bp) - v(pp)) * GND_G;
    let isw = (v(pm) - v(bm)) * 1e-9;
    if (this.state === '정상') isw += (v(pm) - v(bm)) / this.ron();
    const dn = this.diodeNodes();
    if (dn) {
      const id = BODY_IS * (Math.exp(Math.min(this.vd / BODY_VT, 60)) - 1);
      // 다이오드 전류(애노드→캐소드)를 P−→B− 방향으로
      isw += this.state === '과충전 차단' ? id : -id;
    }
    this.iswitch = isw;
    this.current = isw;
    this.voltageDrop = v(pp) - v(pm);

    const ovc = this.num('ovc', 4.3);
    const uvd = this.num('uvd', 2.5);
    const ocp = this.num('ocp', 4);
    const prev = this.state;
    switch (this.state) {
      case '정상':
        if (Math.abs(this.iswitch) > ocp) {
          this.state = '과전류 차단';
          this.timer = 0;
        } else if (this.vcell > ovc) this.state = '과충전 차단';
        else if (this.vcell < uvd) this.state = '과방전 차단';
        break;
      case '과충전 차단':
        if (this.vcell < ovc - 0.2) this.state = '정상';
        break;
      case '과방전 차단':
        if (this.vcell > uvd + 0.5) this.state = '정상';
        break;
      case '과전류 차단':
        this.timer += dt;
        if (this.timer > 1) this.state = '정상';
        break;
    }
    if (prev !== this.state) this.vd = 0;
  }

  override pinCurrents(): number[] {
    return [this.ilink, -this.iswitch, -this.ilink, this.iswitch];
  }

  override get power(): number {
    return this.state === '정상'
      ? this.iswitch * this.iswitch * this.ron()
      : Math.abs(this.iswitch * this.vd);
  }

  get tripped(): boolean {
    return this.state !== '정상';
  }

  info(): string[] {
    return [
      '배터리 보호회로 (BMS)',
      `상태 ${this.state}`,
      `셀 전압 ${fmt(this.vcell, 'V')}`,
      `전류 ${fmt(Math.abs(this.iswitch), 'A')} ${this.iswitch > 1e-6 ? '(방전)' : this.iswitch < -1e-6 ? '(충전)' : ''}`,
      `기준 ${fmt(this.num('uvd', 2.5), 'V')} ~ ${fmt(this.num('ovc', 4.3), 'V')}, ${fmt(this.num('ocp', 4), 'A')}`,
    ];
  }
}
